// dedupe.js — vrstva 2 migračného kernelu: deterministická, READ-ONLY deduplikácia.
//
// Fingerprintuje canonical záznamy podľa ich biznis identity (číslo/dátum/suma/strana,
// provider-agnostický) a klasifikuje každého kandidáta proti tomu, čo už žije v V2 store:
// fresh / exact / near. NIKDY nezapisuje, NIKDY nemaže, NIKDY nesoft-deletuje.
//
// Zámky (NS-002 ARCH §5): #5 read-only (žiadny DELETE/deleted_at) · #6 soft-deleted dvojča
// → near:exact-but-soft-deleted (nikdy sa nevzkriesi) · #11 druhý import toho istého → exact
// (add:0) · #12 near-duplikát sa VŽDY vynorí s reason kódom (nikdy sa ticho nezahodí).
import { db } from '@/v2/db';

// ─── Normalizácia (deduplikačné kľúče) ─────────────────────────────────────────
// číslo/IČO: zhoď VŠETKY biele znaky (vrátane nbsp) + malé písmená → "12 345 678" == "12345678".
function normKey(value) {
  return String(value ?? '').replace(/[\s ]/g, '').toLowerCase();
}
// meno: bez diakritiky, zložené medzery, malé písmená (fallback kľúč keď chýba IČO).
function normName(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ');
}

// ─── Kanonický JSON + sync SHA-256 (reuse tvaru z deps/chain.js, schedule/spec.js) ─────────────
// Fingerprint musí byť SYNCHRÓNNY (kontrakt volá fingerprintRecord bez await) a deterministický
// v prehliadači aj v jeste — preto pure-JS sha256, nie async crypto.subtle.
function canonicalJson(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(value[k])}`).join(',')}}`;
}

export function sha256Hex(message) {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));

  const bytes = [];
  for (let i = 0; i < message.length; i += 1) {
    const c = message.charCodeAt(i);
    if (c < 0x80) bytes.push(c);
    else if (c < 0x800) bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    else bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
  }

  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let i = 7; i >= 0; i -= 1) bytes.push((bitLen / 2 ** (8 * i)) & 0xff);

  const w = new Array(64);
  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i += 1) {
      w[i] = (bytes[off + i * 4] << 24) | (bytes[off + i * 4 + 1] << 16)
        | (bytes[off + i * 4 + 2] << 8) | bytes[off + i * 4 + 3];
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    H[0] = (H[0] + a) | 0; H[1] = (H[1] + b) | 0; H[2] = (H[2] + c) | 0; H[3] = (H[3] + d) | 0;
    H[4] = (H[4] + e) | 0; H[5] = (H[5] + f) | 0; H[6] = (H[6] + g) | 0; H[7] = (H[7] + h) | 0;
  }

  return H.map((x) => (x >>> 0).toString(16).padStart(8, '0')).join('');
}

// ─── Fingerprint ────────────────────────────────────────────────────────────────

/**
 * Fingerprint protistrany = IČO (ak je), inak normalizované meno.
 * IČO vyhráva nad menom — dve strany s rovnakým IČO majú rovnaký fingerprint bez ohľadu na meno.
 */
export function fingerprintCounterparty(cp) {
  const ico = normKey(cp?.ico ?? cp?.counterparty_ico);
  const key = ico ? `ico:${ico}` : `name:${normName(cp?.name ?? cp?.counterparty_name)}`;
  return sha256Hex(key);
}

/**
 * Fingerprint záznamu = sha256 nad normalizovanou biznis n-ticou:
 *   module | document_type | norm(doc number) | entry_date | amount_cents | currency | counterparty
 * Provider v n-tici NIE JE → tá istá faktúra z dvoch systémov má rovnaký fingerprint.
 */
export function fingerprintRecord(record) {
  const cp = fingerprintCounterparty({
    ico: record.counterparty_ico,
    name: record.counterparty_name,
  });
  const tuple = [
    record.module,
    record.document_type,
    normKey(record.doc_number ?? record.number),
    record.entry_date,
    record.amount_cents,
    record.currency,
    cp,
  ];
  return sha256Hex(canonicalJson(tuple));
}

// Slabší kľúč: to isté číslo + tá istá strana, BEZ sumy/dátumu — chytí "rovnaká faktúra, iná suma".
function numberPartyKey(record) {
  const cp = fingerprintCounterparty({
    ico: record.counterparty_ico,
    name: record.counterparty_name,
  });
  return sha256Hex(canonicalJson([
    record.module,
    record.document_type,
    normKey(record.doc_number ?? record.number),
    cp,
  ]));
}

// ─── classify — buckety proti živému store (READ-ONLY) ──────────────────────────

/**
 * Klasifikuje kandidátov proti tomu, čo už žije v store.
 * @returns {{ fresh, exact, near, counterpartyLinks }}
 *   fresh: Candidate[]                                   — nič podobné neexistuje
 *   exact: { candidate, existing_id }[]                  — identická biznis identita (živá)
 *   near:  { candidate, existing_id, reason }[]          — podozrivé, VŽDY s reason kódom
 *   counterpartyLinks: { candidate, existing_id, fill }[] — len ADITÍVNE obohatenie
 */
export async function classify(records, counterparties = [], { since = null } = {}) {
  const fresh = [];
  const exact = [];
  const near = [];
  const counterpartyLinks = [];

  // Cache existujúcich riadkov per module (vrátane soft-deleted — #6 ich potrebuje vidieť).
  const moduleCache = new Map();
  async function existingFor(module) {
    if (!moduleCache.has(module)) {
      const rows = await db.records.where('module').equals(module).toArray();
      const byFp = new Map();
      const byNumParty = new Map();
      for (const row of rows) {
        const fp = fingerprintRecord(row);
        // prvý zápis vyhráva; živý pred zmazaným (živý exact je silnejší signál ako soft-deleted)
        if (!byFp.has(fp) || (byFp.get(fp).deleted_at && !row.deleted_at)) byFp.set(fp, row);
        const npk = numberPartyKey(row);
        if (!byNumParty.has(npk)) byNumParty.set(npk, []);
        byNumParty.get(npk).push(row);
      }
      moduleCache.set(module, { byFp, byNumParty });
    }
    return moduleCache.get(module);
  }

  for (const cand of records) {
    const { byFp, byNumParty } = await existingFor(cand.module);
    const fp = fingerprintRecord(cand);
    const npk = numberPartyKey(cand);
    const hit = byFp.get(fp);

    if (hit) {
      if (hit.deleted_at) {
        // #6 — soft-deleted dvojča: vynor ako near, NIKDY nevzkriesuj (žiadna mutácia deleted_at).
        near.push({ candidate: cand, existing_id: hit.id, reason: 'near:exact-but-soft-deleted' });
      } else {
        // #11 — identická živá faktúra už je: exact, add:0, s odkazom na existujúce id.
        exact.push({ candidate: cand, existing_id: hit.id });
      }
      continue;
    }

    // #12 — rovnaké číslo + strana, iná suma: near, nikdy ticho nezahodené.
    const sameNumberParty = (byNumParty.get(npk) || []).filter((r) => !r.deleted_at);
    if (sameNumberParty.length) {
      near.push({
        candidate: cand,
        existing_id: sameNumberParty[0].id ?? null,
        reason: 'near:same-number-diff-amount',
      });
      continue;
    }

    fresh.push(cand);
    // WITHIN-BATCH dedup (Codex F2): zaregistruj fresh kandidáta do máp, aby ho ĎALŠÍ kandidát
    // v tom istom importe VIDEL. Bez toho dve faktúry s rovnakým číslom v jednom súbore prejdú
    // OBE ako fresh → tichý dvojitý import peňazí. `id: null` = within-batch (ešte necommitnuté).
    byFp.set(fp, { ...cand, id: cand.id ?? null });
    if (!byNumParty.has(npk)) byNumParty.set(npk, []);
    byNumParty.get(npk).push({ ...cand, id: cand.id ?? null });
  }

  // Obohatenie protistrán je LEN ADITÍVNE — dopĺňa prázdne polia existujúcej strany, nikdy
  // neprepisuje neprázdne. Read-only vrstva len navrhne fill; zápis rieši commit vrstva (kernel).
  for (const cp of counterparties) {
    const ico = normKey(cp?.ico);
    if (!ico) continue;
    let existing = null;
    try {
      existing = await db.counterparties.where('ico').equals(ico).first();
    } catch {
      existing = null;
    }
    if (!existing) continue;
    const fill = {};
    for (const field of ['name', 'dic', 'address', 'email', 'iban']) {
      const incoming = cp[field];
      const current = existing[field];
      if (incoming && (current === null || current === undefined || current === '')) {
        fill[field] = incoming;
      }
    }
    if (Object.keys(fill).length) {
      counterpartyLinks.push({ candidate: cp, existing_id: existing.id, fill });
    }
  }

  return { fresh, exact, near, counterpartyLinks };
}

// ─── deterministicId — resumability primitív ────────────────────────────────────

/**
 * Deterministické UUID-v5-tvarové id z (session, fingerprint, ordinal).
 * Rovnaký plán riadok ⇒ rovnaké id, navždy — to je celý základ idempotencie commitu.
 */
export function deterministicId(sessionId, fingerprint, ordinal) {
  const hex = sha256Hex(`${sessionId}:${String(fingerprint)}:${ordinal}`).slice(0, 32);
  const variant = ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
